/**
 * AdminTemplates.js
 * Admin UI for managing page HTML templates (tune pages, article pages, etc.)
 * Templates are stored in the DB and served via /api/content/templates
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import { PAGE_TEMPLATES } from '../templates/pageTemplates';
import {
  isEmailTemplateKey,
  isOrderCannedTemplateKey,
  slugFromOrderCannedKey,
} from '../utils/pageTemplateKeys';
import AdminTuneSiteOptionsPanel from './AdminTuneSiteOptionsPanel';
import './AdminTemplates.css';

// ── PageSearchInput ────────────────────────────────────────────────────────────
// Autocomplete combobox that lets you search pages by title or page ID.
// `value`    – the currently selected page_id string
// `onChange` – called with the new page_id string
// `pages`    – array of { page_id, title, page_type } fetched from the API
// `placeholder` – input placeholder text
const PageSearchInput = ({ value, onChange, pages, placeholder = 'Search pages…' }) => {
  const [query, setQuery]           = useState(value || '');
  const [open, setOpen]             = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const containerRef = useRef(null);
  const inputRef     = useRef(null);

  // Keep query in sync when value is set externally (e.g. initial load)
  useEffect(() => { setQuery(value || ''); }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim().length === 0 ? [] : pages.filter(p => {
    const q = query.toLowerCase();
    return (p.page_id || '').toLowerCase().includes(q) ||
           (p.title   || '').toLowerCase().includes(q);
  }).slice(0, 12);

  const select = (pageId) => {
    setQuery(pageId);
    onChange(pageId);
    setOpen(false);
    setHighlighted(-1);
  };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) {
      if (e.key === 'ArrowDown') { setOpen(true); setHighlighted(0); e.preventDefault(); }
      return;
    }
    if (e.key === 'ArrowDown')  { setHighlighted(h => Math.min(h + 1, filtered.length - 1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp')   { setHighlighted(h => Math.max(h - 1, 0)); e.preventDefault(); }
    else if (e.key === 'Enter')     { if (highlighted >= 0) select(filtered[highlighted].page_id); e.preventDefault(); }
    else if (e.key === 'Escape')    { setOpen(false); setHighlighted(-1); }
  };

  const typeIcon = (type) => {
    if (!type) return '📄';
    if (type.includes('tune')) return '🎵';
    if (type.includes('product')) return '📚';
    if (type.includes('article')) return '📰';
    return '📄';
  };

  return (
    <div className="psi-container" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        className="psi-input"
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        onChange={e => {
          setQuery(e.target.value);
          onChange(e.target.value); // keep parent in sync as user types
          setOpen(true);
          setHighlighted(-1);
        }}
        onFocus={() => { if (query.trim()) setOpen(true); }}
        onKeyDown={handleKeyDown}
      />
      {query && (
        <button
          type="button"
          className="psi-clear"
          onClick={() => { setQuery(''); onChange(''); inputRef.current?.focus(); setOpen(false); }}
          tabIndex={-1}
        >×</button>
      )}
      {open && filtered.length > 0 && (
        <ul className="psi-dropdown">
          {filtered.map((p, i) => (
            <li
              key={p.page_id}
              className={`psi-item ${i === highlighted ? 'highlighted' : ''}`}
              onMouseEnter={() => setHighlighted(i)}
              onMouseDown={(e) => { e.preventDefault(); select(p.page_id); }}
            >
              <span className="psi-item-icon">{typeIcon(p.page_type)}</span>
              <span className="psi-item-body">
                <span className="psi-item-title">{p.title || p.page_id}</span>
                <code className="psi-item-id">{p.page_id}</code>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const API_BASE = '/api/content/templates';

// CSS variable fallbacks so rendered preview looks correct without the full app CSS
const PREVIEW_CSS_VARS = `
:root {
  --color-background: #ffffff;
  --color-text-primary: #1a1a1a;
  --color-text-secondary: #666666;
  --color-border: #e0e0e0;
  --color-accent: #c75b2a;
  --color-accent-light: #fdf0eb;
  --color-surface: #f8f8f8;
  --color-surface-hover: #f2f2f2;
  --color-card-bg: #ffffff;
  --color-header-bg: #1a1a1a;
  --color-footer-bg: #1a1a1a;
  --color-primary: #c75b2a;
  --color-primary-hover: #a8471f;
  --color-link: #c75b2a;
  --color-error: #e53935;
  --color-success: #43a047;
  font-size: 16px;
}
* { box-sizing: border-box; margin: 0; padding: 0; }
body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
       background: #fff; color: #1a1a1a; padding: 0; margin: 0; }
`;

const EMPTY_FORM = {
  template_key: '',
  name: '',
  description: '',
  placeholders: '{}',
  hints: '{}',
  template: '',
  orderCannedSubject: '',
};

/**
 * Builds the full HTML for the srcdoc rendered preview:
 * - Uses form.template (editor content) as primary source; falls back to PAGE_TEMPLATES source
 *   only when the editor content is empty (e.g. creating a new template from scratch)
 * - Injects base href so /api/* and /static/* URLs resolve against localhost
 * - Injects CSS variable fallbacks
 * - Substitutes {{PLACEHOLDER}} values (PAGE_ID falls back to simulatedPageId)
 * - Patches window.location.pathname so inline scripts use the right page ID
 */
function buildRenderedPreviewHtml(templateHtml, testData, simulatedPageId, templateKey) {
  // Use the editor content; only fall back to source code if the editor is empty
  const sourceTemplate = templateKey && PAGE_TEMPLATES[templateKey]?.template;
  let html = templateHtml || sourceTemplate || '';

  const pageId = simulatedPageId || testData['PAGE_ID'] || 'preview-page';

  // Substitute placeholder tokens; PAGE_ID falls back to simulatedPageId
  const effectiveData = { PAGE_ID: pageId, ...testData };
  Object.entries(effectiveData).forEach(([key, value]) => {
    html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value || '');
  });

  // Patch the new-style pathname fallback used by the tune-page inline script so
  // /api/content/tunes/:id/... calls use the simulated page ID (covers the case
  // where data-page-id is still empty after substitution, e.g. preview without testData)
  html = html.replace(
    /var parts = window\.location\.pathname\.split\('\/'\)/,
    `var parts = '/page/${pageId}'.split('/')`
  );
  // Also patch legacy pattern just in case an older template is still in the DB
  html = html.replace(
    /const pathParts = window\.location\.pathname\.split\(['"]\/['"]\)/,
    `const pathParts = '/page/${pageId}'.split('/')`
  );

  const injection = `<base href="http://localhost:3000"><style>${PREVIEW_CSS_VARS}</style>`;
  return injection + html;
}

/** Preview email header/footer fragments inside a minimal table (matches getEmailWrapper layout). */
function buildEmailFragmentPreviewSrcDoc(fragmentHtml, testData) {
  let html = fragmentHtml;
  Object.entries(testData).forEach(([key, value]) => {
    html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value != null ? String(value) : '');
  });
  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>Email preview</title></head>
<body style="margin:0;padding:0;font-family:Helvetica Neue,Helvetica,Arial,sans-serif;background:#f5f5f5">
<table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f5f5;padding:20px 0"><tr><td align="center">
<table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.1)">
${html}
</table></td></tr></table></body></html>`;
}

const AdminTemplates = () => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useToast();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  // edit/create form state
  const [mode, setMode] = useState('list'); // 'list' | 'edit' | 'create'
  /** Main list view tabs (layouts / email / bulk apply). */
  const [listTab, setListTab] = useState('layouts'); // 'layouts' | 'email' | 'tune-options' | 'tunes' | 'articles'
  /** 'page' | 'order-canned' — controls which editor fields are shown */
  const [formKind, setFormKind] = useState('page');
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  // delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Preview state ──────────────────────────────────────────────────────────
  const [previewTab, setPreviewTab] = useState('rendered'); // 'rendered' | 'live'
  const [previewOpen, setPreviewOpen] = useState(false);
  const [allPages, setAllPages] = useState([]);       // for page search combobox
  const [pagesLoaded, setPagesLoaded] = useState(false);
  const [testData, setTestData] = useState({});         // key→value for rendered preview
  const [simulatedPageId, setSimulatedPageId] = useState('');
  const [renderedHtml, setRenderedHtml] = useState(''); // built srcdoc content
  const [livePageId, setLivePageId] = useState('');
  const [livePageUrl, setLivePageUrl] = useState('');
  const [loadingPage, setLoadingPage] = useState(false);
  const iframeRef = useRef(null);

  /** Full inventory of transactional emails (from API) */
  const [emailCatalog, setEmailCatalog] = useState([]);

  /** Article import / theme layouts (separate DB table) */
  const [articleLayouts, setArticleLayouts] = useState([]);
  const [articleLayoutsLoading, setArticleLayoutsLoading] = useState(false);
  const [articleLayoutSeeding, setArticleLayoutSeeding] = useState(false);
  const [editingArticleLayout, setEditingArticleLayout] = useState(null);
  const [articleLayoutSaving, setArticleLayoutSaving] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const canManage = currentUser && currentUser.roles &&
    (currentUser.roles.includes('admin') || currentUser.roles.includes('super_admin'));

  const canEditArticleLayouts = currentUser && currentUser.roles &&
    (currentUser.roles.includes('admin') ||
      currentUser.roles.includes('super_admin') ||
      currentUser.roles.includes('content_creator'));

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE);
      if (!res.ok) throw new Error('Failed to load templates');
      const data = await res.json();
      setTemplates(data);
    } catch (err) {
      showError(err.message || 'Could not load templates');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchEmailCatalog = useCallback(async () => {
    try {
      const res = await fetch('/api/content/templates/email-catalog', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      setEmailCatalog(await res.json());
    } catch {
      /* non-fatal */
    }
  }, [token]);

  useEffect(() => {
    fetchEmailCatalog();
  }, [fetchEmailCatalog]);

  // Initialise test data whenever the placeholders or hints change
  useEffect(() => {
    try {
      const placeholders = JSON.parse(form.placeholders || '{}');
      const hints = JSON.parse(form.hints || '{}');
      setTestData(prev => {
        const next = {};
        Object.keys(placeholders).forEach(k => {
          // keep user-typed value if present, otherwise start with hint text
          next[k] = prev[k] !== undefined ? prev[k] : (hints[k] || '');
        });
        return next;
      });
    } catch {
      // invalid JSON — leave test data as-is
    }
  }, [form.placeholders, form.hints]);

  // Fetch pages list once for the search combobox (lazy – only when preview opens)
  const fetchAllPages = useCallback(async () => {
    if (pagesLoaded) return;
    try {
      const res = await fetch('/api/content/pages', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setAllPages(data || []);
      setPagesLoaded(true);
    } catch { /* non-critical */ }
  }, [pagesLoaded, token]);

  const openPreview = useCallback(() => {
    setPreviewOpen(v => {
      if (!v) fetchAllPages(); // load pages on first open
      return !v;
    });
  }, [fetchAllPages]);

  // ── Form helpers ───────────────────────────────────────────────────────────

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormErrors({});
    setEditingKey(null);
    setFormKind('page');
    setPreviewOpen(false);
    setRenderedHtml('');
    setLivePageUrl('');
    setMode('create');
  };

  const openCreateOrderCanned = () => {
    setListTab('email');
    setForm({
      ...EMPTY_FORM,
      description: 'Canned message for Admin → Orders (order status / follow-up emails)',
      placeholders: JSON.stringify({ customerName: '', orderNumber: '' }, null, 2),
    });
    setFormErrors({});
    setEditingKey(null);
    setFormKind('order-canned');
    setPreviewOpen(false);
    setRenderedHtml('');
    setLivePageUrl('');
    setMode('create');
  };

  const openEdit = (tpl) => {
    const hints = tpl.hints || {};
    const isOrderCanned = isOrderCannedTemplateKey(tpl.template_key);
    setForm({
      template_key: isOrderCanned ? slugFromOrderCannedKey(tpl.template_key) : tpl.template_key,
      name: tpl.name,
      description: tpl.description || '',
      placeholders: JSON.stringify(tpl.placeholders || {}, null, 2),
      hints: JSON.stringify(tpl.hints || {}, null, 2),
      template: tpl.template || '',
      orderCannedSubject: isOrderCanned ? (hints.subject || '') : '',
    });
    setFormErrors({});
    setEditingKey(tpl.template_key);
    setFormKind(isOrderCanned ? 'order-canned' : 'page');
    setPreviewOpen(false);
    setRenderedHtml('');
    setLivePageUrl('');
    setSimulatedPageId('');
    setLivePageId('');
    setMode('edit');
  };

  const cancelEdit = () => {
    setMode('list');
    setEditingKey(null);
    setFormKind('page');
    setForm(EMPTY_FORM);
    setFormErrors({});
    setPreviewOpen(false);
    setRenderedHtml('');
    setLivePageUrl('');
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const errors = {};
    if (formKind === 'order-canned') {
      if (mode === 'create' && !form.template_key.trim()) {
        errors.template_key = 'Slug is required';
      } else if (mode === 'create' && !/^[a-z0-9_-]+$/.test(form.template_key.trim())) {
        errors.template_key = 'Only lowercase letters, numbers, hyphens and underscores';
      }
      if (!form.name.trim()) errors.name = 'Dropdown label is required';
      if (!form.orderCannedSubject.trim()) errors.orderCannedSubject = 'Email subject is required';
      if (!form.template.trim()) errors.template = 'Message body is required';
    } else {
      if (!form.template_key.trim()) errors.template_key = 'Template key is required';
      else if (!/^[a-z0-9-]+$/.test(form.template_key)) errors.template_key = 'Only lowercase letters, numbers and hyphens';
      if (!form.name.trim()) errors.name = 'Name is required';
      if (!form.template.trim()) errors.template = 'HTML template content is required';
      try { JSON.parse(form.placeholders); } catch { errors.placeholders = 'Must be valid JSON'; }
      try { JSON.parse(form.hints); } catch { errors.hints = 'Must be valid JSON'; }
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    let payload;
    if (formKind === 'order-canned') {
      const templateKey = mode === 'edit'
        ? editingKey
        : `order-canned-${form.template_key.trim().toLowerCase()}`;
      payload = {
        template_key: templateKey,
        name: form.name.trim(),
        description: form.description.trim() || 'Canned message for Admin → Orders (order status / follow-up emails)',
        placeholders: { customerName: '', orderNumber: '' },
        hints: {
          subject: form.orderCannedSubject.trim(),
          customerName: 'Customer name (replaced when sending)',
          orderNumber: 'Order number (replaced when sending)',
        },
        template: form.template,
      };
    } else {
      payload = {
        template_key: form.template_key.trim(),
        name: form.name.trim(),
        description: form.description.trim(),
        placeholders: JSON.parse(form.placeholders),
        hints: JSON.parse(form.hints),
        template: form.template
      };
    }
    try {
      let res;
      if (mode === 'create') {
        res = await fetch(API_BASE, { method: 'POST', headers: authHeaders, body: JSON.stringify(payload) });
      } else {
        res = await fetch(`${API_BASE}/${editingKey}`, {
          method: 'PUT',
          headers: authHeaders,
          body: JSON.stringify({ ...payload, new_template_key: payload.template_key })
        });
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      showSuccess(mode === 'create' ? 'Template created!' : 'Template updated!');
      await fetchTemplates();
      cancelEdit();
    } catch (err) {
      showError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const confirmDelete = (tpl) => setDeleteTarget(tpl);
  const cancelDelete = () => setDeleteTarget(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/${deleteTarget.template_key}`, { method: 'DELETE', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      showSuccess('Template deleted');
      setDeleteTarget(null);
      await fetchTemplates();
    } catch (err) {
      showError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // ── Seed defaults ──────────────────────────────────────────────────────────

  const handleSeedDefaults = async () => {
    if (!window.confirm('This will add any missing built-in page, article, email, and order canned message templates (existing rows are not overwritten). Continue?')) return;
    setSeeding(true);
    try {
      const templatesPayload = Object.entries(PAGE_TEMPLATES).map(([key, tpl]) => ({
        template_key: key, name: tpl.name, description: tpl.description || '',
        placeholders: tpl.placeholders || {}, hints: tpl.hints || {}, template: tpl.template || ''
      }));
      const res = await fetch(`${API_BASE}/seed`, { method: 'POST', headers: authHeaders, body: JSON.stringify({ templates: templatesPayload }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Seed failed');
      showSuccess(data.message);
      await fetchTemplates();
    } catch (err) {
      showError(err.message || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  // ── Migrate Pages state ────────────────────────────────────────────────────
  const [tunePages, setTunePages]             = useState([]);
  const [tunePagesLoading, setTunePagesLoading] = useState(false);
  const [selectedPageIds, setSelectedPageIds] = useState(new Set());
  const [migrating, setMigrating]             = useState(false);
  const [migrateResults, setMigrateResults]   = useState(null);

  const loadTunePages = useCallback(async () => {
    setTunePagesLoading(true);
    try {
      const res = await fetch('/api/content/templates/tune-pages-status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load tune pages');
      const data = await res.json();
      setTunePages(data);
      // Pre-select all pages that do NOT yet use the template
      setSelectedPageIds(new Set(data.filter(p => !p.has_template).map(p => p.page_id)));
    } catch (err) {
      showError(err.message);
    } finally {
      setTunePagesLoading(false);
    }
  }, [token, showError]);

  const toggleSelectPage = (id) => {
    setSelectedPageIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = (filter) => {
    if (filter === 'needs') setSelectedPageIds(new Set(tunePages.filter(p => !p.has_template).map(p => p.page_id)));
    else if (filter === 'all')  setSelectedPageIds(new Set(tunePages.map(p => p.page_id)));
    else                        setSelectedPageIds(new Set());
  };

  const handleApplyToSelected = async (dryRun = false) => {
    if (selectedPageIds.size === 0) { showError('Select at least one page'); return; }
    setMigrating(true);
    setMigrateResults(null);
    try {
      const res = await fetch('/api/content/templates/apply-to-tunes', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ page_ids: [...selectedPageIds], dry_run: dryRun })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Migration failed');
      setMigrateResults(data);
      if (!dryRun) {
        showSuccess(`Template applied to ${data.applied} page(s).`);
        await loadTunePages(); // refresh statuses
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setMigrating(false);
    }
  };

  // ── Apply article template to existing article pages ───────────────────────
  const [articlePages, setArticlePages] = useState([]);
  const [articlePagesLoading, setArticlePagesLoading] = useState(false);
  const [selectedArticlePageIds, setSelectedArticlePageIds] = useState(new Set());
  const [articleMigrating, setArticleMigrating] = useState(false);
  const [articleMigrateResults, setArticleMigrateResults] = useState(null);

  const loadArticlePages = useCallback(async () => {
    setArticlePagesLoading(true);
    try {
      const res = await fetch('/api/content/templates/article-pages-status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load article pages');
      const data = await res.json();
      setArticlePages(data);
      setSelectedArticlePageIds(new Set(data.filter(p => !p.has_template).map(p => p.page_id)));
    } catch (err) {
      showError(err.message);
    } finally {
      setArticlePagesLoading(false);
    }
  }, [token, showError]);

  const fetchArticleLayouts = useCallback(async () => {
    setArticleLayoutsLoading(true);
    try {
      const res = await fetch('/api/content/article-layouts');
      if (!res.ok) throw new Error('Failed to load article layouts');
      setArticleLayouts(await res.json());
    } catch (err) {
      showError(err.message || 'Could not load article layouts');
    } finally {
      setArticleLayoutsLoading(false);
    }
  }, [showError]);

  const handleSeedArticleLayouts = async () => {
    setArticleLayoutSeeding(true);
    try {
      const res = await fetch('/api/content/article-layouts/seed', {
        method: 'POST',
        headers: authHeaders
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Seed failed');
      showSuccess(data.message || `Inserted ${data.inserted || 0} new layout(s)`);
      await fetchArticleLayouts();
    } catch (err) {
      showError(err.message);
    } finally {
      setArticleLayoutSeeding(false);
    }
  };

  const openEditArticleLayout = async (layoutKey) => {
    try {
      const res = await fetch(`/api/content/article-layouts/${encodeURIComponent(layoutKey)}`, {
        headers: authHeaders
      });
      if (!res.ok) throw new Error('Could not load layout');
      const row = await res.json();
      setEditingArticleLayout({
        layout_key: row.layout_key,
        name: row.name,
        description: row.description || '',
        import_template: row.import_template || '',
        viewer_css: row.viewer_css || '',
        viewer_wrapper_class: row.viewer_wrapper_class || '',
        options: typeof row.options === 'object' ? JSON.stringify(row.options, null, 2) : (row.options || '{}'),
        sort_order: row.sort_order != null ? row.sort_order : 0
      });
    } catch (e) {
      showError(e.message);
    }
  };

  const saveArticleLayout = async () => {
    if (!editingArticleLayout) return;
    let optionsParsed = {};
    try {
      optionsParsed = JSON.parse(editingArticleLayout.options || '{}');
    } catch {
      showError('Options must be valid JSON (e.g. {"splitFirstParagraph":true})');
      return;
    }
    setArticleLayoutSaving(true);
    try {
      const res = await fetch(`/api/content/article-layouts/${encodeURIComponent(editingArticleLayout.layout_key)}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({
          name: editingArticleLayout.name,
          description: editingArticleLayout.description,
          import_template: editingArticleLayout.import_template,
          viewer_css: editingArticleLayout.viewer_css,
          viewer_wrapper_class: editingArticleLayout.viewer_wrapper_class,
          options: optionsParsed,
          sort_order: Number(editingArticleLayout.sort_order) || 0
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Save failed');
      showSuccess('Article layout saved');
      setEditingArticleLayout(null);
      await fetchArticleLayouts();
    } catch (e) {
      showError(e.message);
    } finally {
      setArticleLayoutSaving(false);
    }
  };

  useEffect(() => {
    setMigrateResults(null);
    setArticleMigrateResults(null);
  }, [listTab]);

  useEffect(() => {
    if (canManage) return;
    if (listTab === 'tunes' || listTab === 'articles') setListTab('layouts');
  }, [canManage, listTab]);

  useEffect(() => {
    if (mode !== 'list') return;
    if (listTab === 'article-layouts') fetchArticleLayouts();
    if (!canManage) return;
    if (listTab === 'tunes') loadTunePages();
    if (listTab === 'articles') loadArticlePages();
  }, [listTab, mode, canManage, loadTunePages, loadArticlePages, fetchArticleLayouts]);

  const toggleSelectArticlePage = (id) => {
    setSelectedArticlePageIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllArticles = (filter) => {
    if (filter === 'needs') setSelectedArticlePageIds(new Set(articlePages.filter(p => !p.has_template).map(p => p.page_id)));
    else if (filter === 'all') setSelectedArticlePageIds(new Set(articlePages.map(p => p.page_id)));
    else setSelectedArticlePageIds(new Set());
  };

  const handleApplyToSelectedArticles = async (dryRun = false) => {
    if (selectedArticlePageIds.size === 0) { showError('Select at least one page'); return; }
    setArticleMigrating(true);
    setArticleMigrateResults(null);
    try {
      const res = await fetch('/api/content/templates/apply-to-articles', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ page_ids: [...selectedArticlePageIds], dry_run: dryRun })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Migration failed');
      setArticleMigrateResults(data);
      if (!dryRun) {
        showSuccess(`Template applied to ${data.applied} article page(s).`);
        await loadArticlePages();
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setArticleMigrating(false);
    }
  };

  // ── JSON formatter ─────────────────────────────────────────────────────────

  const formatJson = (field) => {
    try {
      handleChange(field, JSON.stringify(JSON.parse(form[field]), null, 2));
    } catch { /* leave as-is */ }
  };

  // ── Preview helpers ────────────────────────────────────────────────────────

  const handleRenderPreview = () => {
    const html = isEmailTemplateKey(form.template_key)
      ? buildEmailFragmentPreviewSrcDoc(form.template, testData)
      : buildRenderedPreviewHtml(form.template, testData, simulatedPageId.trim(), form.template_key);
    setRenderedHtml(html);
    setPreviewOpen(true);
    setPreviewTab('rendered');
    // Write the srcdoc to the iframe immediately after render
    setTimeout(() => {
      if (iframeRef.current) {
        iframeRef.current.srcdoc = html;
      }
    }, 50);
  };

  const handleLoadLivePage = () => {
    const id = livePageId.trim();
    if (!id) return;
    setLivePageUrl(`/page/${id}`);
    setPreviewOpen(true);
    setPreviewTab('live');
  };

  // Fetch page data and pre-fill test data fields from existing page
  const handleLoadFromPage = async () => {
    const id = simulatedPageId.trim();
    if (!id) { showError('Enter a page ID first'); return; }
    setLoadingPage(true);
    try {
      const res = await fetch(`/api/content/pages/${id}`);
      if (!res.ok) throw new Error(`Page "${id}" not found`);
      const page = await res.json();
      // Auto-fill placeholder test data from the real page so the preview looks realistic
      setTestData(prev => {
        const next = { ...prev };
        const map = {
          TUNE_TITLE:        page.title,
          TUNE_DESCRIPTION:  page.description,
          TUNE_SUBTITLE:     page.subtitle,
          ORIGINAL_URL:      page.source_url || page.metadata?.source_url || '',
          PAGE_ID:           id,
          NOTATION_ASSET_ID: page.metadata?.notation_asset_id || '',
          AUDIO_ASSET_ID:    page.metadata?.audio_asset_id || '',
          ARTICLE_TITLE:     page.title,
          ARTICLE_BODY:      page.content || '',
        };
        Object.entries(map).forEach(([k, v]) => {
          if (v !== undefined && v !== null && k in next) next[k] = String(v);
        });
        // Always set PAGE_ID if it's a placeholder key in this template
        if ('PAGE_ID' in next) next['PAGE_ID'] = id;
        return next;
      });
      setLivePageId(id);
      showSuccess(`Page "${id}" loaded — click ▶ Render Preview to see it.`);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoadingPage(false);
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const pageLikeTemplates = templates.filter(
    (t) => !isEmailTemplateKey(t.template_key) && !isOrderCannedTemplateKey(t.template_key)
  );
  const emailTemplatesOnly = templates.filter((t) => isEmailTemplateKey(t.template_key));
  const orderCannedTemplatesOnly = templates.filter((t) => isOrderCannedTemplateKey(t.template_key));

  const renderTemplateTable = (rows, sectionId) => (
    <div className="at-table-wrapper" id={sectionId}>
      <table className="at-table">
        <thead>
          <tr>
            <th>Key</th><th>Name</th><th>Description</th><th>Placeholders</th><th>Updated</th>
            {canManage && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(tpl => (
            <tr key={tpl.template_key}>
              <td><code className="at-key">{tpl.template_key}</code></td>
              <td>{tpl.name}</td>
              <td className="at-desc">{tpl.description}</td>
              <td><span className="at-badge">{Object.keys(tpl.placeholders || {}).length} fields</span></td>
              <td className="at-date">{tpl.updated_at ? new Date(tpl.updated_at).toLocaleDateString() : '—'}</td>
              {canManage && (
                <td className="at-actions">
                  <button type="button" className="btn btn-sm btn-outline" onClick={() => openEdit(tpl)}>✏️ Edit</button>
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => confirmDelete(tpl)}>🗑️ Delete</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderOrderCannedTable = () => (
    <div className="at-table-wrapper" id="at-section-order-canned">
      <table className="at-table">
        <thead>
          <tr>
            <th>Key</th>
            <th>Dropdown label</th>
            <th>Email subject</th>
            <th>Updated</th>
            {canManage && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {orderCannedTemplatesOnly.map((tpl) => {
            const subject = (tpl.hints && tpl.hints.subject) || '';
            return (
              <tr key={tpl.template_key}>
                <td><code className="at-key">{tpl.template_key}</code></td>
                <td>{tpl.name}</td>
                <td className="at-desc">{subject || '—'}</td>
                <td className="at-date">{tpl.updated_at ? new Date(tpl.updated_at).toLocaleDateString() : '—'}</td>
                {canManage && (
                  <td className="at-actions">
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => openEdit(tpl)}>✏️ Edit</button>
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => confirmDelete(tpl)}>🗑️ Delete</button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderList = () => (
    <div className="at-list-view">
      <div className="at-toolbar">
        <h2>📄 Page templates</h2>
        <div className="at-toolbar-actions">
          {canManage && (
            <>
              <button type="button" className="btn btn-secondary" onClick={handleSeedDefaults} disabled={seeding}>
                {seeding ? 'Seeding…' : '⚡ Seed Built-in Defaults'}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => { setListTab('layouts'); openCreate(); }}>
                + New Template
              </button>
            </>
          )}
        </div>
      </div>
      <p className="at-subtitle">
        Manage HTML layouts for the editor, article import styles (separate table), transactional email fragments, and bulk-apply tools for tune and article pages.
      </p>

      <div className="at-main-tabs" role="tablist" aria-label="Page templates sections">
        <button
          type="button"
          role="tab"
          id="at-tab-layouts"
          aria-selected={listTab === 'layouts'}
          aria-controls="at-panel-layouts"
          className={`at-tab ${listTab === 'layouts' ? 'active' : ''}`}
          onClick={() => setListTab('layouts')}
        >
          📐 Layouts
        </button>
        <button
          type="button"
          role="tab"
          id="at-tab-article-layouts"
          aria-selected={listTab === 'article-layouts'}
          aria-controls="at-panel-article-layouts"
          className={`at-tab ${listTab === 'article-layouts' ? 'active' : ''}`}
          onClick={() => {
            setListTab('article-layouts');
            setEditingArticleLayout(null);
            setMode('list');
          }}
        >
          📑 Article styles
        </button>
        <button
          type="button"
          role="tab"
          id="at-tab-email"
          aria-selected={listTab === 'email'}
          aria-controls="at-panel-email"
          className={`at-tab ${listTab === 'email' ? 'active' : ''}`}
          onClick={() => setListTab('email')}
        >
          ✉️ Email
        </button>
        {canManage && (
          <button
            type="button"
            role="tab"
            id="at-tab-tune-options"
            aria-selected={listTab === 'tune-options'}
            aria-controls="at-panel-tune-options"
            className={`at-tab ${listTab === 'tune-options' ? 'active' : ''}`}
            onClick={() => {
              setListTab('tune-options');
              setMode('list');
            }}
          >
            🎵 Tune options
          </button>
        )}
        {canManage && (
          <>
            <button
              type="button"
              role="tab"
              id="at-tab-tunes"
              aria-selected={listTab === 'tunes'}
              aria-controls="at-panel-tunes"
              className={`at-tab ${listTab === 'tunes' ? 'active' : ''}`}
              onClick={() => setListTab('tunes')}
            >
              🎵 Tune pages
            </button>
            <button
              type="button"
              role="tab"
              id="at-tab-articles"
              aria-selected={listTab === 'articles'}
              aria-controls="at-panel-articles"
              className={`at-tab ${listTab === 'articles' ? 'active' : ''}`}
              onClick={() => setListTab('articles')}
            >
              📰 Article pages
            </button>
          </>
        )}
      </div>

      <div className="at-tab-panels">
        {listTab === 'layouts' && (
          <div
            role="tabpanel"
            id="at-panel-layouts"
            aria-labelledby="at-tab-layouts"
            className="at-tab-panel"
          >
            {loading ? (
              <div className="at-loading">Loading templates…</div>
            ) : templates.length === 0 ? (
              <div className="at-empty">
                <p>No templates found in the database.</p>
                {canManage && (
                  <button type="button" className="btn btn-secondary" onClick={handleSeedDefaults}>
                    ⚡ Seed Built-in Defaults Now
                  </button>
                )}
              </div>
            ) : (
              <>
                <h3 className="at-section-title">Page layouts</h3>
                <p className="at-section-hint">
                  Tune pages, articles, bios, and other editor-insertable HTML. Keys such as <code>tune-page</code> and <code>article-page</code> are referenced when importing or inserting templates.
                </p>
                {pageLikeTemplates.length === 0 ? (
                  <p className="at-no-placeholders">No page-style templates yet — run <strong>Seed Built-in Defaults</strong>.</p>
                ) : (
                  renderTemplateTable(pageLikeTemplates, 'at-section-pages')
                )}
              </>
            )}
          </div>
        )}

        {listTab === 'article-layouts' && (
          <div
            role="tabpanel"
            id="at-panel-article-layouts"
            aria-labelledby="at-tab-article-layouts"
            className="at-tab-panel"
          >
            <h3 className="at-section-title">Article import &amp; theme layouts</h3>
            <p className="at-section-hint">
              These rows live in the <code>article_layout_templates</code> table (separate from page HTML templates).
              They wrap imported article HTML and supply a <strong>viewer wrapper class</strong> from page metadata{' '}
              <code>article_layout</code>. Placeholders:{' '}
              <code>{'{{METADATA_BLOCK}}'}</code>, <code>{'{{CONTENT}}'}</code>, <code>{'{{BODY_HTML}}'}</code>,{' '}
              <code>{'{{FIRST_PARA_BLOCK}}'}</code>, <code>{'{{AUTHOR}}'}</code>, <code>{'{{TITLE}}'}</code>,{' '}
              <code>{'{{SOURCE}}'}</code>, <code>{'{{SOURCE_HOST}}'}</code>, <code>{'{{SOURCE_FOOTER}}'}</code>.
              Options JSON: <code>{'{"splitFirstParagraph":true}'}</code> enables violin-style first paragraph split.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              {canEditArticleLayouts && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleSeedArticleLayouts}
                  disabled={articleLayoutSeeding}
                >
                  {articleLayoutSeeding ? 'Seeding…' : '⚡ Seed built-in article styles'}
                </button>
              )}
            </div>

            {editingArticleLayout && (
              <div className="at-article-layout-editor" style={{ marginBottom: '2rem', padding: '1rem', border: '1px solid var(--color-border)', borderRadius: '8px' }}>
                <h4>Edit <code>{editingArticleLayout.layout_key}</code></h4>
                <div className="form-group" style={{ marginTop: '0.75rem' }}>
                  <label>Name</label>
                  <input
                    className="form-control"
                    value={editingArticleLayout.name}
                    onChange={(e) => setEditingArticleLayout({ ...editingArticleLayout, name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input
                    className="form-control"
                    value={editingArticleLayout.description}
                    onChange={(e) => setEditingArticleLayout({ ...editingArticleLayout, description: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Sort order</label>
                  <input
                    type="number"
                    className="form-control"
                    value={editingArticleLayout.sort_order}
                    onChange={(e) => setEditingArticleLayout({ ...editingArticleLayout, sort_order: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Viewer wrapper class (CSS)</label>
                  <input
                    className="form-control"
                    value={editingArticleLayout.viewer_wrapper_class}
                    onChange={(e) => setEditingArticleLayout({ ...editingArticleLayout, viewer_wrapper_class: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Options (JSON)</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={editingArticleLayout.options}
                    onChange={(e) => setEditingArticleLayout({ ...editingArticleLayout, options: e.target.value })}
                    spellCheck={false}
                  />
                </div>
                <div className="form-group">
                  <label>Viewer / import CSS</label>
                  <textarea
                    className="form-control"
                    rows={14}
                    value={editingArticleLayout.viewer_css}
                    onChange={(e) => setEditingArticleLayout({ ...editingArticleLayout, viewer_css: e.target.value })}
                    spellCheck={false}
                  />
                </div>
                <div className="form-group">
                  <label>Import HTML template</label>
                  <textarea
                    className="form-control"
                    rows={16}
                    value={editingArticleLayout.import_template}
                    onChange={(e) => setEditingArticleLayout({ ...editingArticleLayout, import_template: e.target.value })}
                    spellCheck={false}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveArticleLayout}
                    disabled={articleLayoutSaving || !canEditArticleLayouts}
                  >
                    {articleLayoutSaving ? 'Saving…' : 'Save layout'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setEditingArticleLayout(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {articleLayoutsLoading ? (
              <div className="at-loading">Loading article layouts…</div>
            ) : articleLayouts.length === 0 ? (
              <p className="at-no-placeholders">
                No article layouts in the database yet.{' '}
                {canEditArticleLayouts
                  ? 'Run “Seed built-in article styles” above.'
                  : 'Ask an editor or admin to seed defaults.'}
              </p>
            ) : (
              <div className="at-table-wrapper">
                <table className="at-table">
                  <thead>
                    <tr>
                      <th>Key</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Order</th>
                      {canEditArticleLayouts && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {articleLayouts.map((row) => (
                      <tr key={row.layout_key}>
                        <td><code className="at-key">{row.layout_key}</code></td>
                        <td>{row.name}</td>
                        <td className="at-desc">{row.description}</td>
                        <td>{row.sort_order}</td>
                        {canEditArticleLayouts && (
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              onClick={() => openEditArticleLayout(row.layout_key)}
                            >
                              Edit
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {listTab === 'email' && (
          <div
            role="tabpanel"
            id="at-panel-email"
            aria-labelledby="at-tab-email"
            className="at-tab-panel"
          >
            {loading ? (
              <div className="at-loading">Loading templates…</div>
            ) : templates.length === 0 ? (
              <div className="at-empty">
                <p>No templates found in the database.</p>
                {canManage && (
                  <button type="button" className="btn btn-secondary" onClick={handleSeedDefaults}>
                    ⚡ Seed Built-in Defaults Now
                  </button>
                )}
              </div>
            ) : (
              <>
                <h3 className="at-section-title">Order status canned messages</h3>
                <p className="at-section-hint">
                  Plain-text templates for the <strong>Select Canned Message Template</strong> dropdown in Admin → Orders (status updates and follow-up emails). Use <code>{'{customerName}'}</code> and <code>{'{orderNumber}'}</code> in the subject and body — they are replaced when you send.
                </p>
                {canManage && (
                  <div className="at-section-toolbar">
                    <button type="button" className="btn btn-primary btn-sm" onClick={openCreateOrderCanned}>
                      + New order message
                    </button>
                  </div>
                )}
                {orderCannedTemplatesOnly.length === 0 ? (
                  <p className="at-no-placeholders">
                    No <code>order-canned-*</code> rows yet — click <strong>Seed Built-in Defaults</strong> above to add the standard shipping, refund, and follow-up messages.
                  </p>
                ) : (
                  renderOrderCannedTable()
                )}

                <h3 className="at-section-title">Editable email templates (database)</h3>
                <p className="at-section-hint">
                  Header/footer wrap most messages. Body rows use placeholders such as <code>{'{{RESET_LINK}}'}</code> or <code>{'{{VERIFICATION_URL}}'}</code> — see each row’s JSON hints. Run <strong>Seed Built-in Defaults</strong> to add any missing keys (existing rows are not overwritten).
                </p>
                {emailTemplatesOnly.length === 0 ? (
                  <p className="at-no-placeholders">
                    No <code>email-*</code> rows yet — seed to add headers, footers, orders/registrations/newsletter bodies, bug reports, back-in-stock, etc.
                  </p>
                ) : (
                  renderTemplateTable(emailTemplatesOnly, 'at-section-email')
                )}
              </>
            )}

            <h3 className="at-section-title">All automated emails</h3>
            <p className="at-section-hint">
              Complete inventory: what each message is for, whether it uses a template key from the table above, and where it is implemented. Order confirmations from Stripe and some staff digests are still assembled only in code.
            </p>
            {emailCatalog.length === 0 ? (
              <p className="at-no-placeholders">
                Catalog could not be loaded. You need <code>content.view</code> (or higher) and a valid session.
              </p>
            ) : (
              <div className="at-table-wrapper at-email-catalog-table">
                <table className="at-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Message</th>
                      <th>Template key</th>
                      <th>Source file</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailCatalog.map((row) => (
                      <tr key={row.id}>
                        <td>{row.category}</td>
                        <td>
                          {row.name}
                          {row.wired === false && (
                            <span className="at-email-catalog-badge code-only">code only</span>
                          )}
                          {row.description && (
                            <div className="at-email-catalog-desc">{row.description}</div>
                          )}
                        </td>
                        <td>
                          {row.templateKey ? (
                            <code className="at-key">{row.templateKey}</code>
                          ) : (
                            <span className="at-muted">—</span>
                          )}
                        </td>
                        <td className="at-desc"><small>{row.source}</small></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {canManage && listTab === 'tune-options' && (
          <div
            role="tabpanel"
            id="at-panel-tune-options"
            aria-labelledby="at-tab-tune-options"
            className="at-tab-panel"
          >
            <AdminTuneSiteOptionsPanel authHeaders={authHeaders} />
          </div>
        )}

        {canManage && listTab === 'tunes' && (
          <div
            role="tabpanel"
            id="at-panel-tunes"
            aria-labelledby="at-tab-tunes"
            className="at-tab-panel at-tab-panel--migrate"
          >
            <p className="at-tab-panel-intro">
              Re-apply the stored <code>tune-page</code> template to existing tune-notation pages. Notation and audio asset IDs are taken from metadata or current HTML.
            </p>
            <div className="at-migrate-body at-migrate-body--flush">
              {tunePagesLoading ? (
                <div className="at-loading">Loading tune pages…</div>
              ) : tunePages.length === 0 ? (
                <p className="at-no-placeholders">No tune-notation pages found in the database.</p>
              ) : (
                <>
                  <div className="at-migrate-summary">
                    <span className="at-migrate-stat ok">
                      ✅ {tunePages.filter(p => p.has_template).length} already using template
                    </span>
                    <span className="at-migrate-stat warn">
                      ⚠ {tunePages.filter(p => !p.has_template).length} need migration
                    </span>
                    <div className="at-migrate-selectors">
                      <button type="button" className="btn btn-xs btn-ghost" onClick={() => selectAll('needs')}>Select needs migration</button>
                      <button type="button" className="btn btn-xs btn-ghost" onClick={() => selectAll('all')}>Select all</button>
                      <button type="button" className="btn btn-xs btn-ghost" onClick={() => selectAll('none')}>Clear</button>
                    </div>
                  </div>

                  <div className="at-migrate-list">
                    {tunePages.map(p => (
                      <label key={p.page_id} className={`at-migrate-row ${p.has_template ? 'has-tpl' : 'needs-tpl'}`}>
                        <input
                          type="checkbox"
                          checked={selectedPageIds.has(p.page_id)}
                          onChange={() => toggleSelectPage(p.page_id)}
                        />
                        <span className="at-migrate-status">{p.has_template ? '✅' : '⚠'}</span>
                        <span className="at-migrate-title">{p.title || p.page_id}</span>
                        <code className="at-migrate-id">{p.page_id}</code>
                        <span className="at-migrate-assets">
                          {p.notation_id && <span title="Notation asset ID">🖼 {p.notation_id}</span>}
                          {p.audio_id && <span title="Audio asset ID">🔊 {p.audio_id}</span>}
                        </span>
                        <a
                          href={`/page/${p.page_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="at-migrate-preview-link"
                          onClick={e => e.stopPropagation()}
                        >↗</a>
                      </label>
                    ))}
                  </div>

                  <div className="at-migrate-actions">
                    <span className="at-migrate-selected">{selectedPageIds.size} selected</span>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleApplyToSelected(true)}
                      disabled={migrating || selectedPageIds.size === 0}
                    >
                      {migrating ? '…' : '🔍 Dry Run'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => handleApplyToSelected(false)}
                      disabled={migrating || selectedPageIds.size === 0}
                    >
                      {migrating ? 'Applying…' : `🔄 Apply to ${selectedPageIds.size} page(s)`}
                    </button>
                  </div>

                  {migrateResults && (
                    <div className="at-migrate-results">
                      <div className={`at-migrate-result-header ${migrateResults.dry_run ? 'dry' : 'live'}`}>
                        {migrateResults.dry_run
                          ? `🔍 Dry run — would update ${migrateResults.would_apply} page(s)`
                          : `✅ Applied template to ${migrateResults.applied} page(s)`}
                        {migrateResults.errors > 0 && ` · ⚠ ${migrateResults.errors} error(s)`}
                      </div>
                      <div className="at-migrate-result-list">
                        {migrateResults.results.map(r => (
                          <div key={r.page_id} className={`at-migrate-result-row ${r.status}`}>
                            <span>{r.status === 'ok' ? '✅' : '❌'}</span>
                            <code>{r.page_id}</code>
                            <span>{r.title}</span>
                            {r.status === 'ok' && (
                              <span className="at-result-detail">
                                notation:{r.notation_id || '—'} · audio:{r.audio_id || '—'} · {r.subtitle}
                              </span>
                            )}
                            {r.status === 'error' && <span className="at-result-error">{r.error}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {canManage && listTab === 'articles' && (
          <div
            role="tabpanel"
            id="at-panel-articles"
            aria-labelledby="at-tab-articles"
            className="at-tab-panel at-tab-panel--migrate"
          >
            <p className="at-tab-panel-intro">
              Re-apply the stored <code>article-page</code> template to pages with <code>page_type = article</code>. Fields are parsed from existing HTML when possible.
            </p>
            <div className="at-migrate-body at-migrate-body--flush">
              {articlePagesLoading ? (
                <div className="at-loading">Loading article pages…</div>
              ) : articlePages.length === 0 ? (
                <p className="at-no-placeholders">No article pages found in the database.</p>
              ) : (
                <>
                  <div className="at-migrate-summary">
                    <span className="at-migrate-stat ok">
                      ✅ {articlePages.filter(p => p.has_template).length} already using template marker
                    </span>
                    <span className="at-migrate-stat warn">
                      ⚠ {articlePages.filter(p => !p.has_template).length} may need migration
                    </span>
                    <div className="at-migrate-selectors">
                      <button type="button" className="btn btn-xs btn-ghost" onClick={() => selectAllArticles('needs')}>Select needs migration</button>
                      <button type="button" className="btn btn-xs btn-ghost" onClick={() => selectAllArticles('all')}>Select all</button>
                      <button type="button" className="btn btn-xs btn-ghost" onClick={() => selectAllArticles('none')}>Clear</button>
                    </div>
                  </div>

                  <div className="at-migrate-list">
                    {articlePages.map(p => (
                      <label key={p.page_id} className={`at-migrate-row ${p.has_template ? 'has-tpl' : 'needs-tpl'}`}>
                        <input
                          type="checkbox"
                          checked={selectedArticlePageIds.has(p.page_id)}
                          onChange={() => toggleSelectArticlePage(p.page_id)}
                        />
                        <span className="at-migrate-status">{p.has_template ? '✅' : '⚠'}</span>
                        <span className="at-migrate-title">{p.title || p.page_id}</span>
                        <code className="at-migrate-id">{p.page_id}</code>
                        <a
                          href={`/page/${p.page_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="at-migrate-preview-link"
                          onClick={e => e.stopPropagation()}
                        >↗</a>
                      </label>
                    ))}
                  </div>

                  <div className="at-migrate-actions">
                    <span className="at-migrate-selected">{selectedArticlePageIds.size} selected</span>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => handleApplyToSelectedArticles(true)}
                      disabled={articleMigrating || selectedArticlePageIds.size === 0}
                    >
                      {articleMigrating ? '…' : '🔍 Dry Run'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => handleApplyToSelectedArticles(false)}
                      disabled={articleMigrating || selectedArticlePageIds.size === 0}
                    >
                      {articleMigrating ? 'Applying…' : `🔄 Apply to ${selectedArticlePageIds.size} page(s)`}
                    </button>
                  </div>

                  {articleMigrateResults && (
                    <div className="at-migrate-results">
                      <div className={`at-migrate-result-header ${articleMigrateResults.dry_run ? 'dry' : 'live'}`}>
                        {articleMigrateResults.dry_run
                          ? `🔍 Dry run — would update ${articleMigrateResults.would_apply} page(s)`
                          : `✅ Applied template to ${articleMigrateResults.applied} page(s)`}
                        {articleMigrateResults.errors > 0 && ` · ⚠ ${articleMigrateResults.errors} error(s)`}
                      </div>
                      <div className="at-migrate-result-list">
                        {articleMigrateResults.results.map(r => (
                          <div key={r.page_id} className={`at-migrate-result-row ${r.status}`}>
                            <span>{r.status === 'ok' ? '✅' : '❌'}</span>
                            <code>{r.page_id}</code>
                            <span>{r.title}</span>
                            {r.status === 'ok' && r.title_extracted && (
                              <span className="at-result-detail">title: {r.title_extracted}</span>
                            )}
                            {r.status === 'error' && <span className="at-result-error">{r.error}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPreviewSection = () => {
    const placeholderKeys = Object.keys(testData);

    return (
      <div className="at-preview-section">
        <div className="at-preview-section-header" onClick={openPreview}>
          <span className="at-preview-toggle-icon">{previewOpen ? '▼' : '▶'}</span>
          <h3>🖥 Preview</h3>
          <span className="at-preview-header-hint">
            {previewOpen ? 'Click to collapse' : 'Render template with test data or view a live page'}
          </span>
        </div>

        {previewOpen && (
          <div className="at-preview-body">
            {/* ── Tabs ── */}
            <div className="at-preview-tabs">
              <button
                className={`at-tab ${previewTab === 'rendered' ? 'active' : ''}`}
                onClick={() => setPreviewTab('rendered')}
              >
                🖥 Rendered Preview
              </button>
              <button
                className={`at-tab ${previewTab === 'live' ? 'active' : ''}`}
                onClick={() => setPreviewTab('live')}
              >
                🌐 Live Page
              </button>
            </div>

            {/* ── Rendered Preview tab ── */}
            {previewTab === 'rendered' && (
              <div className="at-rendered-tab">
                <div className="at-preview-controls">
                  <div className="at-preview-controls-left">
                    <span className="at-control-label">Test data</span>
                    <span className="at-control-hint">Fill in values to substitute into the template</span>
                  </div>
                  <div className="at-preview-controls-right">
                    <div className="at-simpage-row">
                      <label>Simulate page ID:</label>
                      <PageSearchInput
                        value={simulatedPageId}
                        onChange={setSimulatedPageId}
                        pages={allPages}
                        placeholder="Search pages…"
                      />
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={handleLoadFromPage}
                        disabled={loadingPage}
                        title="Verify this page exists in the database"
                      >
                        {loadingPage ? '…' : '✓ Verify'}
                      </button>
                    </div>
                  </div>
                </div>

                {placeholderKeys.length > 0 ? (
                  <div className="at-test-data-grid">
                    {placeholderKeys.map(key => (
                      <div key={key} className="at-test-field">
                        <label>{key}</label>
                        <input
                          type="text"
                          value={testData[key] || ''}
                          onChange={e => setTestData(prev => ({ ...prev, [key]: e.target.value }))}
                          placeholder={`{{${key}}}`}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="at-no-placeholders">No placeholders defined — template will render as-is.</p>
                )}

                <div className="at-render-bar">
                  <button type="button" className="btn btn-primary" onClick={handleRenderPreview}>
                    ▶ Render Preview
                  </button>
                  <span className="at-render-hint">
                    {isEmailTemplateKey(form.template_key)
                      ? 'Email fragments are shown inside a sample 600px message shell. Use realistic test values for logo URL and gradient.'
                      : 'Previews your edited template HTML. Enter a page ID above — test data is auto-filled and related sections (Featured In, Related Tunes…) load live from the API.'}
                  </span>
                </div>

                {renderedHtml ? (
                  <div className="at-iframe-wrapper">
                    <iframe
                      ref={iframeRef}
                      className="at-preview-iframe"
                      title="Template Rendered Preview"
                      sandbox="allow-scripts allow-same-origin"
                      srcDoc={renderedHtml}
                    />
                  </div>
                ) : (
                  <div className="at-iframe-placeholder">
                    <span>Fill in the test data above and click <strong>▶ Render Preview</strong></span>
                  </div>
                )}
              </div>
            )}

            {/* ── Live Page tab ── */}
            {previewTab === 'live' && (
              <div className="at-live-tab">
                <div className="at-live-controls">
                  <div className="at-live-row">
                    <label>Page:</label>
                    <PageSearchInput
                      value={livePageId}
                      onChange={setLivePageId}
                      pages={allPages}
                      placeholder="Search by title or page ID…"
                    />
                    <button className="btn btn-primary btn-sm" onClick={handleLoadLivePage}>
                      Load Page
                    </button>
                    {livePageUrl && (
                      <a
                        href={livePageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-ghost"
                      >
                        ↗ Open in new tab
                      </a>
                    )}
                  </div>
                  <p className="at-live-hint">
                    Shows the <em>currently saved</em> version of the page. Save your template changes first, then recreate a page with the new template to see updates here.
                  </p>
                </div>

                {livePageUrl ? (
                  <div className="at-iframe-wrapper">
                    <iframe
                      className="at-preview-iframe"
                      title="Live Page Preview"
                      src={livePageUrl}
                      sandbox="allow-scripts allow-same-origin allow-forms"
                    />
                  </div>
                ) : (
                  <div className="at-iframe-placeholder">
                    <span>Enter a page ID and click <strong>Load Page</strong> to preview it here</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderForm = () => {
    const isOrderCannedForm = formKind === 'order-canned';
    return (
    <div className="at-form-view">
      <div className="at-form-header">
        <button className="btn btn-ghost" onClick={cancelEdit}>← Back to list</button>
        <h2>
          {mode === 'create'
            ? (isOrderCannedForm ? '+ New order canned message' : '+ New Template')
            : (isOrderCannedForm ? `✏️ Edit order message: ${editingKey}` : `✏️ Edit: ${editingKey}`)}
        </h2>
        <div className="at-form-header-actions">
          <button className="btn btn-secondary" onClick={cancelEdit} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : '💾 Save Template'}
          </button>
        </div>
      </div>

      <div className="at-form-fields">
        {isOrderCannedForm ? (
          <>
            <div className="at-field-row">
              <div className={`at-field ${formErrors.template_key ? 'has-error' : ''}`}>
                <label>Slug {mode === 'create' && <span className="required">*</span>}</label>
                <input
                  type="text"
                  value={form.template_key}
                  onChange={e => handleChange('template_key', e.target.value)}
                  placeholder="e.g. shipping_delay"
                  disabled={mode === 'edit'}
                />
                {formErrors.template_key && <span className="at-error">{formErrors.template_key}</span>}
                <small>Stored as <code>order-canned-{'{slug}'}</code>. Lowercase letters, numbers, hyphens, underscores.</small>
              </div>
              <div className={`at-field ${formErrors.name ? 'has-error' : ''}`}>
                <label>Dropdown label <span className="required">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => handleChange('name', e.target.value)}
                  placeholder="e.g. Shipping Delay"
                />
                {formErrors.name && <span className="at-error">{formErrors.name}</span>}
                <small>Shown in Admin → Orders canned message dropdown.</small>
              </div>
            </div>

            <div className={`at-field ${formErrors.orderCannedSubject ? 'has-error' : ''}`}>
              <label>Email subject <span className="required">*</span></label>
              <input
                type="text"
                value={form.orderCannedSubject}
                onChange={e => handleChange('orderCannedSubject', e.target.value)}
                placeholder="e.g. Update on Your Order Shipping"
              />
              {formErrors.orderCannedSubject && <span className="at-error">{formErrors.orderCannedSubject}</span>}
              <small>You may use <code>{'{customerName}'}</code> and <code>{'{orderNumber}'}</code> in the subject.</small>
            </div>

            <div className={`at-field at-field-full ${formErrors.template ? 'has-error' : ''}`}>
              <label>Message body <span className="required">*</span></label>
              <textarea
                className="at-html-editor"
                rows={20}
                value={form.template}
                onChange={e => handleChange('template', e.target.value)}
                placeholder={'Hello {customerName},\n\nRegarding your order {orderNumber}...'}
                spellCheck={false}
              />
              {formErrors.template && <span className="at-error">{formErrors.template}</span>}
              <small>Plain text. Use <code>{'{customerName}'}</code> and <code>{'{orderNumber}'}</code> where needed.</small>
            </div>
          </>
        ) : (
          <>
        <div className="at-field-row">
          <div className={`at-field ${formErrors.template_key ? 'has-error' : ''}`}>
            <label>Template Key <span className="required">*</span></label>
            <input
              type="text"
              value={form.template_key}
              onChange={e => handleChange('template_key', e.target.value)}
              placeholder="e.g. tune-page"
              disabled={mode === 'edit'}
            />
            {formErrors.template_key && <span className="at-error">{formErrors.template_key}</span>}
            <small>Lowercase letters, numbers, hyphens only. Used as the identifier in the editor.</small>
          </div>
          <div className={`at-field ${formErrors.name ? 'has-error' : ''}`}>
            <label>Name <span className="required">*</span></label>
            <input
              type="text"
              value={form.name}
              onChange={e => handleChange('name', e.target.value)}
              placeholder="e.g. Tune Page Template"
            />
            {formErrors.name && <span className="at-error">{formErrors.name}</span>}
          </div>
        </div>

        <div className="at-field">
          <label>Description</label>
          <input
            type="text"
            value={form.description}
            onChange={e => handleChange('description', e.target.value)}
            placeholder="Short description shown in the editor dropdown"
          />
        </div>

        <div className="at-field-row">
          <div className={`at-field ${formErrors.placeholders ? 'has-error' : ''}`}>
            <div className="at-field-label-row">
              <label>Placeholders (JSON)</label>
              <button type="button" className="btn btn-xs btn-ghost" onClick={() => formatJson('placeholders')}>Format</button>
            </div>
            <textarea
              className="at-json-editor"
              rows={8}
              value={form.placeholders}
              onChange={e => handleChange('placeholders', e.target.value)}
              placeholder={'{\n  "TUNE_TITLE": "",\n  "PAGE_ID": ""\n}'}
              spellCheck={false}
            />
            {formErrors.placeholders && <span className="at-error">{formErrors.placeholders}</span>}
            <small>Keys used as <code>{'{{KEY}}'}</code> in the template HTML.</small>
          </div>
          <div className={`at-field ${formErrors.hints ? 'has-error' : ''}`}>
            <div className="at-field-label-row">
              <label>Hints (JSON)</label>
              <button type="button" className="btn btn-xs btn-ghost" onClick={() => formatJson('hints')}>Format</button>
            </div>
            <textarea
              className="at-json-editor"
              rows={8}
              value={form.hints}
              onChange={e => handleChange('hints', e.target.value)}
              placeholder={'{\n  "TUNE_TITLE": "e.g. Fenella"\n}'}
              spellCheck={false}
            />
            {formErrors.hints && <span className="at-error">{formErrors.hints}</span>}
            <small>Placeholder text shown in the insert-template form for each field.</small>
          </div>
        </div>

        <div className={`at-field at-field-full ${formErrors.template ? 'has-error' : ''}`}>
          <label>HTML Template <span className="required">*</span></label>
          <textarea
            className="at-html-editor"
            rows={28}
            value={form.template}
            onChange={e => handleChange('template', e.target.value)}
            placeholder="<!-- Paste or write your HTML template here. Use {{PLACEHOLDER_KEY}} for dynamic values. -->"
            spellCheck={false}
          />
          {formErrors.template && <span className="at-error">{formErrors.template}</span>}
          <small>Use <code>{'{{PLACEHOLDER_KEY}}'}</code> for dynamic values editors fill in when inserting the template.</small>
        </div>
          </>
        )}
      </div>

      {/* ── Preview section (page/email HTML templates only) ── */}
      {!isOrderCannedForm && renderPreviewSection()}
    </div>
    );
  };

  const renderDeleteModal = () => (
    <div className="at-modal-overlay" onClick={cancelDelete}>
      <div className="at-modal" onClick={e => e.stopPropagation()}>
        <h3>Delete Template</h3>
        <p>Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?</p>
        <p className="at-warning">⚠️ This cannot be undone. Existing pages built from this template are not affected.</p>
        <div className="at-modal-actions">
          <button className="btn btn-secondary" onClick={cancelDelete} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Yes, Delete'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="admin-templates container">
      <div className="content-card">
        {mode === 'list' ? renderList() : renderForm()}
        {deleteTarget && renderDeleteModal()}
      </div>
    </div>
  );
};

export default AdminTemplates;
